import { clsx } from "clsx";
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface TabItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
}

export function Tabs({ tabs, activeTab, onChange, className }: TabsProps) {
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [hasInitialized, setHasInitialized] = useState(false);

  const updateIndicator = useCallback(() => {
    const nav = navRef.current;
    const activeEl = tabRefs.current.get(activeTab);
    if (!nav || !activeEl) {
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const tabRect = activeEl.getBoundingClientRect();
    setIndicator({
      left: tabRect.left - navRect.left + nav.scrollLeft,
      width: tabRect.width,
    });
    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [activeTab, hasInitialized]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  return (
    <div className={clsx("border-b border-edge", className)} role="tablist">
      <nav
        ref={navRef}
        className="relative -mb-px flex gap-1 overflow-x-auto"
        aria-label="Zakładki"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabRefs.current.set(tab.id, el);
                } else {
                  tabRefs.current.delete(tab.id);
                }
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                onChange(tab.id);
              }}
              className={clsx(
                "flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-150",
                isActive ? "text-adara-700" : "text-dim hover:text-sub",
              )}
            >
              {tab.icon && (
                <span
                  className={clsx(isActive ? "text-adara-700" : "text-faint")}
                >
                  {tab.icon}
                </span>
              )}
              {tab.label}
            </button>
          );
        })}
        <span
          className="absolute bottom-0 h-0.5 bg-adara-700"
          style={{
            left: indicator.left,
            width: indicator.width,
            transition: hasInitialized
              ? "left 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1)"
              : "none",
          }}
        />
      </nav>
    </div>
  );
}
